import { ApiResponse } from '../types/api'

// API Configuration
export const API_CONFIG = {
  BASE_URL: process.env.REACT_APP_API_URL || 'http://khabeer.local/api',
  TENANT_DOMAIN_SUFFIX: process.env.REACT_APP_TENANT_DOMAIN_SUFFIX || (
    process.env.NODE_ENV === 'production' ? '.khabeer-tech.com' : '.local'
  ),
  // Prefer HTTPS in production and for khabeer-tech.com domains
  PROTOCOL: (process.env.REACT_APP_API_PROTOCOL
    || ((process.env.NODE_ENV === 'production' || (process.env.REACT_APP_TENANT_DOMAIN_SUFFIX || '').includes('khabeer-tech.com')) ? 'https' : 'http')) as 'http' | 'https',
  TIMEOUT: 30000,
  HEADERS: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
}

// Tenant URL helper with dynamic domain suffix
export const getTenantApiUrl = (tenantName?: string): string => {
  const tenant = tenantName || localStorage.getItem('tenant_name')
  
  if (!tenant) {
    throw new Error('Tenant name is required for API calls. Please set tenant_name in localStorage or pass it explicitly.')
  }

  return `${API_CONFIG.PROTOCOL}://${tenant}${API_CONFIG.TENANT_DOMAIN_SUFFIX}/api`
}

// Get base API URL with fallback handling
export const getBaseApiUrl = (): string => {
  return getTenantApiUrl()
}

// Auth token management
let authToken: string | null = null

export const setAuthToken = (token: string | null) => {
  authToken = token
  if (token) {
    localStorage.setItem('auth_token', token)
  } else {
    localStorage.removeItem('auth_token')
  }
}

export const getAuthToken = (): string | null => {
  if (authToken) return authToken
  return localStorage.getItem('auth_token')
}

export const clearAuthToken = () => {
  authToken = null
  localStorage.removeItem('auth_token')
}

// HTTP Client class
export class ApiClient {
  private timeout: number
  private defaultHeaders: Record<string, string>

  constructor(config = API_CONFIG) {
    this.timeout = config.TIMEOUT
    this.defaultHeaders = { ...config.HEADERS }
  }

  // Dynamic base URL that uses tenant subdomain
  private get baseUrl(): string {
    return getTenantApiUrl()
  }

  private getHeaders(): Record<string, string> {
    const headers = { ...this.defaultHeaders }
    const token = getAuthToken()
    
    if (token) {
      headers.Authorization = `Bearer ${token}`
    }
    
    return headers
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`
    const headers = this.getHeaders()

    // Merge headers
    const finalHeaders = {
      ...headers,
      ...(options.headers || {}),
    }

    // Setup request options
    const requestOptions: RequestInit = {
      ...options,
      headers: finalHeaders,
      signal: AbortSignal.timeout(this.timeout),
    }

    try {
      const response = await fetch(url, requestOptions)
      const contentType = response.headers.get('content-type')
      
      // Handle non-JSON responses
      if (!contentType?.includes('application/json')) {
        throw new ApiError('Invalid response format', [], response.status)
      }

      const data = await response.json()

      // Handle HTTP errors
      if (!response.ok) {
        const error = new ApiError(
          data.message || `HTTP ${response.status}: ${response.statusText}`,
          data.errors || [],
          response.status,
          data.code
        )
        throw error
      }

      return data as ApiResponse<T>
    } catch (error) {
      if (error instanceof ApiError) {
        throw error
      }

      // Handle network/timeout errors
      if (error instanceof DOMException && error.name === 'TimeoutError') {
        throw new ApiError('Request timeout', [], 408)
      }

      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new ApiError('Network error - please check your connection', [], 0)
      }

      // Generic error
      throw new ApiError(
        error instanceof Error ? error.message : 'Unknown error occurred',
        [],
        500
      )
    }
  }

  // HTTP Methods
  async get<T>(endpoint: string, params?: Record<string, any>): Promise<ApiResponse<T>> {
    let url = endpoint
    
    if (params) {
      const searchParams = new URLSearchParams()
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            value.forEach(v => searchParams.append(`${key}[]`, String(v)))
          } else {
            searchParams.append(key, String(value))
          }
        }
      })
      
      const queryString = searchParams.toString()
      if (queryString) {
        url += `?${queryString}`
      }
    }

    return this.request<T>(url, { method: 'GET' })
  }

  async post<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    const options: RequestInit = {
      method: 'POST',
    }

    if (data instanceof FormData) {
      options.body = data
      // Remove Content-Type header for FormData (browser will set it with boundary)
      const headers = this.getHeaders()
      delete headers['Content-Type']
      options.headers = headers
    } else if (data) {
      options.body = JSON.stringify(data)
    }

    return this.request<T>(endpoint, options)
  }

  async put<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    const options: RequestInit = {
      method: 'PUT',
    }

    if (data instanceof FormData) {
      options.body = data
      const headers = this.getHeaders()
      delete headers['Content-Type']
      options.headers = headers
    } else if (data) {
      options.body = JSON.stringify(data)
    }

    return this.request<T>(endpoint, options)
  }

  async patch<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    const options: RequestInit = {
      method: 'PATCH',
    }

    if (data) {
      options.body = JSON.stringify(data)
    }

    return this.request<T>(endpoint, options)
  }

  async delete<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    const options: RequestInit = {
      method: 'DELETE',
    }

    if (data) {
      options.body = JSON.stringify(data)
    }

    return this.request<T>(endpoint, options)
  }
}

// Custom ApiError class
export class ApiError extends Error {
  public errors: Array<{field: string, message: string, code?: string}>
  public status: number
  public code?: string

  constructor(
    message: string,
    errors: Array<{field: string, message: string, code?: string}> = [],
    status: number = 500,
    code?: string
  ) {
    super(message)
    this.name = 'ApiError'
    this.errors = errors
    this.status = status
    this.code = code
  }

  isValidationError(): boolean {
    return this.status === 422 && this.errors.length > 0
  }

  isAuthError(): boolean {
    return this.status === 401 || this.status === 403
  }

  isNetworkError(): boolean {
    return this.status === 0
  }

  getFieldError(field: string): string | null {
    const error = this.errors.find(e => e.field === field)
    return error?.message || null
  }

  getAllFieldErrors(): Record<string, string> {
    return this.errors.reduce((acc, error) => {
      acc[error.field] = error.message
      return acc
    }, {} as Record<string, string>)
  }
}

// Create default client instance
export const apiClient = new ApiClient()

// Utility functions
export const handleApiError = (error: unknown): ApiError => {
  if (error instanceof ApiError) {
    return error
  }
  
  if (error instanceof Error) {
    return new ApiError(error.message)
  }
  
  return new ApiError('Unknown error occurred')
}

export const isApiError = (error: unknown): error is ApiError => {
  return error instanceof ApiError
}

// Response helpers
export const extractData = <T>(response: ApiResponse<T>): T => {
  return response.data
}

export const extractMessage = <T>(response: ApiResponse<T>): string => {
  return response.message
}

// Retry mechanism
export const withRetry = async <T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  delay: number = 1000
): Promise<T> => {
  let lastError: Error = new Error('Operation failed after retries')

  for (let i = 0; i <= maxRetries; i++) {
    try {
      return await operation()
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Unknown error')
      
      // Don't retry on auth errors or validation errors
      if (error instanceof ApiError && (error.isAuthError() || error.isValidationError())) {
        throw error
      }
      
      // Wait before retry (except on last attempt)
      if (i < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, delay * (i + 1)))
      }
    }
  }

  throw lastError
}

// File upload helper
export const createFormData = (data: Record<string, any>): FormData => {
  const formData = new FormData()
  
  Object.entries(data).forEach(([key, value]) => {
    if (value instanceof File) {
      formData.append(key, value)
    } else if (Array.isArray(value)) {
      value.forEach((item, index) => {
        if (typeof item === 'object' && item !== null) {
          Object.entries(item).forEach(([subKey, subValue]) => {
            formData.append(`${key}[${index}][${subKey}]`, String(subValue))
          })
        } else {
          formData.append(`${key}[]`, String(item))
        }
      })
    } else if (typeof value === 'object' && value !== null) {
      Object.entries(value).forEach(([subKey, subValue]) => {
        formData.append(`${key}[${subKey}]`, String(subValue))
      })
    } else if (value !== undefined && value !== null) {
      formData.append(key, String(value))
    }
  })
  
  return formData
} 